import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '../../core/usePageMeta';
import { ROUTES } from '../../app/routes';
import { publicApi, type DirectorySearch, type OrganizationCard, type Paged, type PublicCategoryGroup } from './publicApi';
import { EmptyState, ErrorState, HubSearchBar, LoadingGrid, OrganizationCardView, PublicLayout, usePublicLanguage } from './components';

const DirectoryMap = lazy(() => import('./DirectoryMap'));
const PAGE_SIZE = 24;

function num(value: string | null): number | undefined {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Directory search (`/search`), category page (`/c/:categoryCode`) and city page (`/city/:city`).
 * Filters live in the URL so results are shareable and the back button works.
 */
export function SearchPage() {
  const { t } = useTranslation();
  const lang = usePublicLanguage();
  const navigate = useNavigate();
  const { categoryCode, city: cityParam } = useParams();
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState<PublicCategoryGroup[]>([]);
  const [result, setResult] = useState<Paged<OrganizationCard> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [showMap, setShowMap] = useState(false);

  const search: DirectorySearch = useMemo(() => ({
    q: params.get('q') ?? undefined,
    category: (categoryCode ?? params.get('category') ?? undefined)?.toUpperCase(),
    city: cityParam ?? params.get('city') ?? undefined,
    lat: num(params.get('lat')),
    lng: num(params.get('lng')),
    bookableOnline: params.get('bookableOnline') === 'true',
    hasShop: params.get('hasShop') === 'true',
    hasJobs: params.get('hasJobs') === 'true',
    sort: (params.get('sort') as DirectorySearch['sort']) ?? undefined,
    page: num(params.get('page')) ?? 1,
    pageSize: PAGE_SIZE,
  }), [params, categoryCode, cityParam]);

  useEffect(() => {
    let active = true;
    publicApi.categories(lang).then((data) => { if (active) setGroups(data); }).catch(() => undefined);
    return () => { active = false; };
  }, [lang]);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError(null);
    publicApi.organizations(lang, search)
      .then((data) => { if (active) setResult(data); })
      .catch(() => { if (active) setError(t('hub.search.loadError', 'Search results could not be loaded.')); });
    return () => { active = false; };
  }, [lang, search, attempt, t]);

  function update(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  }

  function chooseCategory(code: string) {
    // Category lives in the path on /c/:code; move between the two forms without losing filters.
    const rest = new URLSearchParams(params);
    rest.delete('category');
    rest.delete('page');
    const query = rest.toString();
    if (!code) navigate(`${ROUTES.search}${query ? `?${query}` : ''}`);
    else navigate(`${ROUTES.category.replace(':categoryCode', code.toLowerCase())}${query ? `?${query}` : ''}`);
  }

  const allCategories = groups.flatMap((group) => [{ code: group.code, name: group.name, group: true }, ...group.leaves.map((leaf) => ({ ...leaf, group: false }))]);
  const categoryName = allCategories.find((c) => c.code === search.category)?.name;
  const heading = categoryName ?? (search.city ? t('hub.search.inCity', 'Businesses in {{city}}', { city: search.city }) : search.q
    ? t('hub.search.resultsFor', 'Results for “{{q}}”', { q: search.q }) : t('hub.search.title', 'All businesses'));
  usePageMeta(heading, { noindex: Boolean(search.q) });
  const pages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return <PublicLayout showHeaderSearch={false}>
    <div className="hub-wrap" style={{ paddingTop: 20 }}>
      <HubSearchBar initialQuery={search.q ?? ''} initialCity={search.city ?? ''} />
    </div>
    <div className="hub-wrap hub-search-layout">
      <aside className="hub-filters" aria-label={t('hub.search.filters', 'Filters')}>
        <label>{t('hub.search.category', 'Category')}
          <select value={search.category ?? ''} onChange={(event) => chooseCategory(event.target.value)}>
            <option value="">{t('hub.search.anyCategory', 'All categories')}</option>
            {groups.map((group) => <optgroup key={group.code} label={group.name}>
              <option value={group.code}>{t('hub.search.allIn', 'All {{name}}', { name: group.name })}</option>
              {group.leaves.map((leaf) => <option key={leaf.code} value={leaf.code}>{leaf.name}</option>)}
            </optgroup>)}
          </select>
        </label>
        <label>{t('hub.search.sort', 'Sort by')}
          <select value={search.sort ?? 'recommended'} onChange={(event) => update('sort', event.target.value === 'recommended' ? null : event.target.value)}>
            <option value="recommended">{t('hub.search.sortRecommended', 'Recommended')}</option>
            <option value="name">{t('hub.search.sortName', 'Name')}</option>
            {search.lat != null && <option value="distance">{t('hub.search.sortDistance', 'Distance')}</option>}
          </select>
        </label>
        <label className="check"><input type="checkbox" checked={!!search.bookableOnline} onChange={(event) => update('bookableOnline', event.target.checked ? 'true' : null)} />
          {t('hub.search.bookable', 'Book online')}</label>
        <label className="check"><input type="checkbox" checked={!!search.hasShop} onChange={(event) => update('hasShop', event.target.checked ? 'true' : null)} />
          {t('hub.search.shop', 'Has a shop')}</label>
        <label className="check"><input type="checkbox" checked={!!search.hasJobs} onChange={(event) => update('hasJobs', event.target.checked ? 'true' : null)} />
          {t('hub.search.openJobs', 'Hiring now')}</label>
        <button type="button" className="hub-btn sm ghost" onClick={() => setShowMap((value) => !value)} aria-pressed={showMap}>
          {showMap ? t('hub.search.hideMap', 'Hide map') : t('hub.search.showMap', 'Show map')}
        </button>
      </aside>

      <section aria-labelledby="hub-results">
        <div className="hub-section-head">
          <h1 id="hub-results" className="hub-page-title">{heading}</h1>
          {result && <span className="hub-muted" aria-live="polite">{t('hub.search.count', '{{count}} results', { count: result.total })}</span>}
        </div>
        {error && <ErrorState message={error} onRetry={() => setAttempt((value) => value + 1)} />}
        {!result && !error && <LoadingGrid count={6} label={t('hub.loading', 'Loading')} />}
        {result && showMap && result.items.length > 0 && <div className="hub-map" style={{ height: 340, marginBottom: 18, padding: 0 }}>
          <Suspense fallback={<span>{t('hub.loading', 'Loading')}</span>}>
            <DirectoryMap cards={result.items} center={search.lat != null && search.lng != null ? { lat: search.lat, lng: search.lng } : undefined} />
          </Suspense>
        </div>}
        {result && result.items.length === 0 && <EmptyState>{t('hub.search.empty', 'No businesses match these filters. Try another category or city.')}</EmptyState>}
        {result && result.items.length > 0 && <div className="hub-grid">{result.items.map((card) => <OrganizationCardView key={card.id} card={card} />)}</div>}
        {result && pages > 1 && <nav className="hub-pager" aria-label={t('hub.search.pages', 'Pages')}>
          <button type="button" className="hub-btn sm ghost" disabled={result.page <= 1} onClick={() => update('page', String(result.page - 1))}>
            {t('hub.search.previous', 'Previous')}</button>
          <span>{t('hub.search.pageOf', 'Page {{page}} of {{pages}}', { page: result.page, pages })}</span>
          <button type="button" className="hub-btn sm ghost" disabled={result.page >= pages} onClick={() => update('page', String(result.page + 1))}>
            {t('hub.search.next', 'Next')}</button>
        </nav>}
      </section>
    </div>
  </PublicLayout>;
}
